import { create } from 'zustand';
import { persist } from 'zustand/middleware';

console.log('[composeTabsStore] Module loading...');

export interface ComposeTab {
  id: string;
  subject: string;
  preheader: string;
  to: Array<{ name?: string; address: string }>;
  cc: Array<{ name?: string; address: string }>;
  bcc: Array<{ name?: string; address: string }>;
  body: string;
  toInputValue: string;
  ccInputValue: string;
  bccInputValue: string;
  showBcc: boolean;
  replyTo?: string;
  createdAt: number;
}

interface ComposeTabsState {
  tabs: ComposeTab[];
  activeTabId: string | null;
  showingCompose: boolean;
  chatOpen: boolean;
  viewingChat: boolean; // Track if currently viewing chat (vs mail)
  docsOpen: boolean;
  viewingDocs: boolean;
  sheetsOpen: boolean;
  viewingSheets: boolean;
  
  // Actions
  createTab: (options?: { replyTo?: string; subject?: string; to?: Array<{ name?: string; address: string }>; cc?: Array<{ name?: string; address: string }>; body?: string }) => string;
  getOrCreateTab: (options?: { replyTo?: string; subject?: string; to?: Array<{ name?: string; address: string }>; cc?: Array<{ name?: string; address: string }>; body?: string }) => string;
  closeTab: (tabId: string) => void;
  setActiveTab: (tabId: string) => void;
  setShowingCompose: (showing: boolean) => void;
  updateTab: (tabId: string, updates: Partial<ComposeTab>) => void;
  getActiveTab: () => ComposeTab | undefined;
  hasOpenTabs: () => boolean;
  getTabCount: () => number;
  clearAllTabs: () => void;
  cleanupEmptyTabs: () => void;
  openChat: () => void;
  closeChat: () => void;
  setViewingChat: (viewing: boolean) => void;
  openDocs: () => void;
  closeDocs: () => void;
  setViewingDocs: (viewing: boolean) => void;
  openSheets: () => void;
  closeSheets: () => void;
  setViewingSheets: (viewing: boolean) => void;
}

const generateTabId = () => `compose-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export const useComposeTabsStore = create<ComposeTabsState>()(
  persist(
    (set, get) => ({
      tabs: [],
      activeTabId: null,
      showingCompose: false,
      chatOpen: false,
      viewingChat: false,
      docsOpen: false,
      viewingDocs: false,
      sheetsOpen: false,
      viewingSheets: false,

      createTab: (options) => {
        console.log('[composeTabsStore] createTab called');
        const newTab: ComposeTab = {
          id: generateTabId(),
          subject: options?.subject || '',
          preheader: '',
          to: options?.to || [],
          cc: options?.cc || [],
          bcc: [],
          body: options?.body || '',
          toInputValue: '',
          ccInputValue: '',
          bccInputValue: '',
          showBcc: false,
          replyTo: options?.replyTo,
          createdAt: Date.now(),
        };

        set((state) => ({
          tabs: [...state.tabs, newTab],
          activeTabId: newTab.id,
          showingCompose: true,
        }));

        return newTab.id;
      },

      // Zoho-style: reuse an existing empty tab if available, otherwise create new
      getOrCreateTab: (options) => {
        const state = get();
        const hasContent = options?.subject || (options?.to && options.to.length > 0) || options?.body;
        
        // If no specific content requested, look for an existing empty tab to reuse
        if (!hasContent) {
          const emptyTab = state.tabs.find(
            (tab) => !tab.subject && tab.to.length === 0
          );
          if (emptyTab) {
            set({ activeTabId: emptyTab.id, showingCompose: true });
            return emptyTab.id;
          }
        }
        
        // Create a new tab
        return get().createTab(options);
      },

      closeTab: (tabId) => {
        set((state) => {
          const newTabs = state.tabs.filter((t) => t.id !== tabId);
          let newActiveId = state.activeTabId;

          if (state.activeTabId === tabId) {
            // Find next tab to activate
            const closedIndex = state.tabs.findIndex(t => t.id === tabId);
            if (newTabs.length > 0) {
              const newIndex = Math.max(0, closedIndex - 1);
              newActiveId = newTabs[newIndex]?.id || newTabs[0]?.id || null;
            } else {
              newActiveId = null;
            }
          }

          return {
            tabs: newTabs,
            activeTabId: newActiveId,
            showingCompose: newTabs.length > 0 ? state.showingCompose : false,
          };
        });
      },

      setActiveTab: (tabId) => {
        set({ activeTabId: tabId, showingCompose: true, viewingChat: false, viewingDocs: false, viewingSheets: false });
      },

      setShowingCompose: (showing) => {
        set({ showingCompose: showing, viewingChat: showing ? false : get().viewingChat, viewingDocs: showing ? false : get().viewingDocs, viewingSheets: showing ? false : get().viewingSheets });
      },

      updateTab: (tabId, updates) => {
        set((state) => ({
          tabs: state.tabs.map((tab) =>
            tab.id === tabId ? { ...tab, ...updates } : tab
          ),
        }));
      },

      getActiveTab: () => {
        const state = get();
        return state.tabs.find((t) => t.id === state.activeTabId);
      },

      hasOpenTabs: () => {
        return get().tabs.length > 0;
      },

      getTabCount: () => {
        return get().tabs.length;
      },

      clearAllTabs: () => {
        set({ tabs: [], activeTabId: null, showingCompose: false });
      },

      // Remove tabs with no subject and no recipients (signature-only body doesn't count as content)
      cleanupEmptyTabs: () => {
        set((state) => {
          const nonEmptyTabs = state.tabs.filter(
            (tab) => tab.subject || tab.to.length > 0
          );
          const newActiveId = nonEmptyTabs.length > 0
            ? (nonEmptyTabs.find(t => t.id === state.activeTabId)?.id || nonEmptyTabs[0].id)
            : null;
          // If there are no tabs left and showingCompose was true, turn it off
          const shouldShowCompose = state.showingCompose && nonEmptyTabs.length > 0;
          console.log('[composeTabsStore] cleanupEmptyTabs - removed:', state.tabs.length - nonEmptyTabs.length, 'kept:', nonEmptyTabs.length, 'showingCompose:', state.showingCompose, '->', shouldShowCompose);
          return { 
            tabs: nonEmptyTabs, 
            activeTabId: newActiveId,
            showingCompose: shouldShowCompose,
          };
        });
      },

      openChat: () => {
        console.log('[composeTabsStore] openChat called');
        set({ chatOpen: true, viewingChat: true, viewingDocs: false, viewingSheets: false, showingCompose: false });
      },

      closeChat: () => {
        console.log('[composeTabsStore] closeChat called');
        set({ chatOpen: false, viewingChat: false });
      },
      
      setViewingChat: (viewing: boolean) => {
        console.log('[composeTabsStore] setViewingChat called:', viewing);
        if (viewing && !get().chatOpen) {
          set({ chatOpen: true, viewingChat: true, viewingDocs: false, viewingSheets: false, showingCompose: false });
        } else {
          set({ viewingChat: viewing, viewingDocs: viewing ? false : get().viewingDocs, viewingSheets: viewing ? false : get().viewingSheets, showingCompose: viewing ? false : get().showingCompose });
        }
      },

      openDocs: () => {
        set({ docsOpen: true, viewingDocs: true, viewingChat: false, viewingSheets: false, showingCompose: false });
      },
      closeDocs: () => {
        set({ docsOpen: false, viewingDocs: false });
      },
      setViewingDocs: (viewing: boolean) => {
        if (viewing && !get().docsOpen) {
          set({ docsOpen: true, viewingDocs: true, viewingChat: false, viewingSheets: false, showingCompose: false });
        } else {
          set({ viewingDocs: viewing, viewingChat: viewing ? false : get().viewingChat, viewingSheets: viewing ? false : get().viewingSheets, showingCompose: viewing ? false : get().showingCompose });
        }
      },

      openSheets: () => {
        set({ sheetsOpen: true, viewingSheets: true, viewingChat: false, viewingDocs: false, showingCompose: false });
      },
      closeSheets: () => {
        set({ sheetsOpen: false, viewingSheets: false });
      },
      setViewingSheets: (viewing: boolean) => {
        if (viewing && !get().sheetsOpen) {
          set({ sheetsOpen: true, viewingSheets: true, viewingChat: false, viewingDocs: false, showingCompose: false });
        } else {
          set({ viewingSheets: viewing, viewingChat: viewing ? false : get().viewingChat, viewingDocs: viewing ? false : get().viewingDocs, showingCompose: viewing ? false : get().showingCompose });
        }
      },
    }),
    {
      name: 'zmail-compose-tabs',
      version: 2,
      migrate: (persistedState: unknown, _version: number) => {
        if (persistedState && typeof persistedState === 'object') {
          const ps = persistedState as Record<string, unknown>;
          delete ps.viewingChat;
          delete ps.viewingDocs;
          delete ps.viewingSheets;
        }
        return persistedState as ComposeTabsState;
      },
      merge: (persistedState: unknown, currentState: ComposeTabsState) => {
        const merged = { ...currentState, ...((persistedState as Partial<ComposeTabsState>) || {}) } as ComposeTabsState;
        merged.viewingChat = false;
        merged.viewingDocs = false;
        merged.viewingSheets = false;
        return merged;
      },
      partialize: (state) => ({
        tabs: state.tabs,
        activeTabId: state.activeTabId,
        showingCompose: state.showingCompose,
        chatOpen: state.chatOpen,
        // viewingChat NOT persisted — overlay state should reset on reload, route is source of truth
        docsOpen: state.docsOpen,
        // viewingDocs NOT persisted
        sheetsOpen: state.sheetsOpen,
        // viewingSheets NOT persisted
      }),
      onRehydrateStorage: () => {
        console.log('[composeTabsStore] Starting hydration from localStorage');
        return (state, error) => {
          if (error) {
            console.error('[composeTabsStore] Hydration error:', error);
          } else if (state) {
            console.log('[composeTabsStore] Hydration complete - chatOpen:', state.chatOpen, 'showingCompose:', state.showingCompose, 'tabs:', state.tabs.length);
            console.log('[composeTabsStore] NOT running cleanup - preserving persisted tabs');
            // Don't cleanup persisted tabs - if user saved them, they want them
          }
        };
      },
    }
  )
);

console.log('[composeTabsStore] Store created, checking localStorage...');
if (typeof window !== 'undefined') {
  const stored = localStorage.getItem('zmail-compose-tabs');
  console.log('[composeTabsStore] localStorage value:', stored);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      console.log('[composeTabsStore] Parsed localStorage:', parsed);
    } catch (e) {
      console.error('[composeTabsStore] Failed to parse localStorage:', e);
    }
  }
}

// Log initial state on load
if (typeof window !== 'undefined') {
  setTimeout(() => {
    const state = useComposeTabsStore.getState();
    console.log('[composeTabsStore] Initial state check - chatOpen:', state.chatOpen, 'showingCompose:', state.showingCompose);
  }, 10);
}
